import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/hooks/useOrganization';
import { FeatureFlagsService } from '@/services/featureFlags';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME_AUTH } from '@/lib/queryCacheConfig';
import type { FeatureFlag, FeatureFlagName } from '@/types/featureFlags';

export function useFeatureFlag() {
  const { currentOrganization } = useOrganization();
  const slug = currentOrganization?.slug ?? '';

  const {
    data: flags = [],
    isPending,
    error,
  } = useQuery<FeatureFlag[]>({
    queryKey: queryKeys.featureFlags(slug),
    queryFn: () => FeatureFlagsService.getAllFeatureFlags(slug),
    enabled: !!slug,
    staleTime: STALE_TIME_AUTH.featureFlags,
  });

  const flagsMap = useMemo(() => {
    const map = new Map<string, FeatureFlag>();
    flags.forEach((flag) => map.set(flag.featureName, flag));
    return map;
  }, [flags]);

  const isEnabled = useCallback(
    (featureName: FeatureFlagName) => flagsMap.get(featureName)?.isEnabled ?? false,
    [flagsMap],
  );

  const getFlag = useCallback(
    (featureName: FeatureFlagName) => flagsMap.get(featureName),
    [flagsMap],
  );

  return {
    flags,
    isLoading: isPending,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    isEnabled,
    getFlag,
  };
}
